import { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import { RefreshCw, Filter, X, Radio } from "lucide-react";
import { fetchRansomwareAnalytics, type RansomwareResponse } from "../../../api/status";
import { generateEditorialSummary } from "../../../utils/ransomwareUtils";

import BriefingStrip from "./BriefingStrip";
import ActivityChart from "./ActivityChart";
import GroupsLeaderboard from "./GroupsLeaderboard";
import SectorsPanel from "./SectorsPanel";
import GroupDrawer from "./GroupDrawer";

interface RansomwareSectionProps {
  timeframe?: string;
  onTimeframeChange?: (newTf: string) => void;
}

const TIMEFRAME_OPTIONS = [
  { value: "24h", label: "24H" },
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
  { value: "3m", label: "3M" },
  { value: "6m", label: "6M" },
];

export default function RansomwareSection({
  timeframe: externalTimeframe,
  onTimeframeChange,
}: RansomwareSectionProps) {
  const [internalTimeframe, setInternalTimeframe] = useState<string>("30d");
  const timeframe = externalTimeframe || internalTimeframe;

  // Cross-filtering states
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedSector, setSelectedSector] = useState<string | null>(null);

  // Group drawer state
  const [drawerGroup, setDrawerGroup] = useState<string | null>(null);

  // Live update counter
  const [secondsAgo, setSecondsAgo] = useState<number>(0);

  // SWR for data fetching with stale-while-revalidate caching (prevents layout shift on period switch)
  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<RansomwareResponse>(
    ['ransomware', timeframe],
    () => fetchRansomwareAnalytics({ timeframe }),
    {
      refreshInterval: 60000,
      keepPreviousData: true,
      onSuccess: () => setSecondsAgo(0),
    }
  );

  // Live timer interval
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsAgo((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSelectTimeframe = (newTf: string) => {
    if (onTimeframeChange) {
      onTimeframeChange(newTf);
    } else {
      setInternalTimeframe(newTf);
    }
  };

  // Rule-based editorial briefing sentence from computed real data
  const briefingSentence = useMemo(() => {
    if (!data) return "";
    return generateEditorialSummary(data);
  }, [data]);

  // Filtered actors based on selected sector
  const filteredActors = useMemo(() => {
    const actorsList = Array.isArray(data?.actors) ? data.actors : [];
    if (!selectedSector) return actorsList;
    return actorsList.filter((actor) =>
      Array.isArray(actor?.sectors) &&
      actor.sectors.some((s) => typeof s === "string" && s.toLowerCase() === selectedSector.toLowerCase())
    );
  }, [data?.actors, selectedSector]);

  // Filtered sectors based on selected group
  const filteredSectors = useMemo(() => {
    const sectorsList = Array.isArray(data?.sectors) ? data.sectors : [];
    if (!selectedGroup) return sectorsList;
    const actorsList = Array.isArray(data?.actors) ? data.actors : [];
    const grp = actorsList.find((a) => typeof a?.name === "string" && a.name.toLowerCase() === selectedGroup.toLowerCase());
    if (!grp || !Array.isArray(grp.sectors)) return sectorsList;
    return sectorsList.filter((sec) =>
      grp.sectors.some((s) => typeof s === "string" && s.toLowerCase() === sec.name.toLowerCase())
    );
  }, [data?.sectors, data?.actors, selectedGroup]);

  const totalAttacks = data?.summary?.attacks || 0;

  return (
    <section
      className="rw-section-container"
      style={{
        marginTop: 28,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* ── SECTION HEADER & CONTROLS ── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          paddingBottom: 16,
          borderBottom: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {/* TITLE & LIVE FEED INDICATOR */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h2
                style={{
                  fontSize: "1.35rem",
                  fontWeight: 800,
                  color: "var(--dash-text-primary, #f8fafc)",
                  letterSpacing: "-0.01em",
                  margin: 0,
                }}
              >
                Ransomware Intelligence
              </h2>

              {/* LIVE BADGE */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  padding: "4px 12px",
                  borderRadius: 999,
                  background: "rgba(34, 197, 94, 0.1)",
                  color: "#22c55e",
                  border: "1px solid rgba(34, 197, 94, 0.25)",
                  letterSpacing: "0.04em",
                }}
                title="Live telemetry feed"
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#22c55e",
                    boxShadow: "0 0 8px #22c55e",
                  }}
                />
                <span>Live • updated {secondsAgo}s ago</span>
              </div>

              {/* REVALIDATING INDICATOR */}
              {isValidating && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: "var(--dash-indigo, #818cf8)",
                  }}
                >
                  <Radio size={12} className="animate-spin" />
                  <span>Refreshing...</span>
                </div>
              )}
            </div>
          </div>

          {/* TIMEFRAME SEGMENTED CONTROL */}
          <div
            style={{
              display: "inline-flex",
              padding: 3,
              background: "var(--dash-card-bg, rgba(15, 23, 42, 0.7))",
              border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.1))",
              borderRadius: 8,
              backdropFilter: "blur(8px)",
            }}
            role="tablist"
            aria-label="Ransomware time period"
          >
            {TIMEFRAME_OPTIONS.map((opt) => {
              const isActive = timeframe === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelectTimeframe(opt.value)}
                  role="tab"
                  aria-selected={isActive}
                  style={{
                    padding: "6px 14px",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    borderRadius: 6,
                    background: isActive ? "var(--dash-indigo, #6366F1)" : "transparent",
                    color: isActive ? "#ffffff" : "var(--dash-text-muted, #94a3b8)",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* RULE-BASED EDITORIAL BRIEFING SENTENCE */}
        {briefingSentence && (
          <div
            style={{
              fontSize: "0.88rem",
              fontWeight: 500,
              color: "var(--dash-text-muted, #cbd5e1)",
              lineHeight: 1.5,
              paddingTop: 4,
            }}
          >
            {briefingSentence}
          </div>
        )}
      </div>

      {/* ── CROSS-FILTER CHIPS BAR ── */}
      {(selectedGroup || selectedSector) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            borderRadius: 8,
            background: "rgba(0, 102, 224, 0.1)",
            border: "1px solid rgba(0, 102, 224, 0.25)",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "var(--dash-indigo, #818cf8)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            <Filter size={14} /> Active Filter:
          </span>

          {selectedGroup && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.78rem",
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: 6,
                background: "var(--dash-indigo, #6366F1)",
                color: "#ffffff",
              }}
            >
              Group: {selectedGroup}
              <X
                size={13}
                onClick={() => setSelectedGroup(null)}
                style={{ cursor: "pointer", opacity: 0.9 }}
                aria-label="Remove group filter"
              />
            </span>
          )}

          {selectedSector && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.78rem",
                fontWeight: 700,
                padding: "4px 10px",
                borderRadius: 6,
                background: "var(--dash-indigo, #6366F1)",
                color: "#ffffff",
              }}
            >
              Sector: {selectedSector}
              <X
                size={13}
                onClick={() => setSelectedSector(null)}
                style={{ cursor: "pointer", opacity: 0.9 }}
                aria-label="Remove sector filter"
              />
            </span>
          )}

          <button
            onClick={() => {
              setSelectedGroup(null);
              setSelectedSector(null);
            }}
            style={{
              marginLeft: "auto",
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "var(--dash-indigo, #818cf8)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── ERROR STATE ── */}
      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 10,
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
            color: "#ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: "0.88rem", fontWeight: 600 }}>
            Unable to load ransomware threat intelligence data.
          </span>
          <button
            onClick={() => void mutate()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 6,
              background: "#ef4444",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 700,
            }}
          >
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      {/* ── FIRST LOAD SKELETON ── */}
      {isLoading && !data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ height: 110, borderRadius: 12, background: "var(--dash-subcard-bg, rgba(255,255,255,0.03))", opacity: 0.6 }} />
          <div style={{ height: 340, borderRadius: 12, background: "var(--dash-subcard-bg, rgba(255,255,255,0.03))", opacity: 0.6 }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ height: 440, borderRadius: 12, background: "var(--dash-subcard-bg, rgba(255,255,255,0.03))", opacity: 0.6 }} />
            <div style={{ height: 440, borderRadius: 12, background: "var(--dash-subcard-bg, rgba(255,255,255,0.03))", opacity: 0.6 }} />
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT ── */}
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* BRIEFING STRIP */}
          <BriefingStrip data={data} />

          {/* EMPTY TIMEFRAME STATE */}
          {totalAttacks === 0 ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                background: "var(--dash-card-bg, rgba(15, 23, 42, 0.6))",
                border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--dash-text-primary, #f8fafc)" }}>
                No ransomware activity recorded for the last {timeframe}.
              </div>
              <p style={{ fontSize: "0.85rem", color: "var(--dash-text-muted, #94a3b8)", marginTop: 6, maxWidth: 480 }}>
                No verified site disclosures occurred in this period. Try selecting a broader time frame (30D or 3M).
              </p>
            </div>
          ) : (
            <>
              {/* ACTIVITY TIMELINE CHART */}
              <ActivityChart
                daily={data.daily}
                peakDay={data.summary.peakDay}
                selectedGroup={selectedGroup}
                selectedSector={selectedSector}
              />

              {/* GROUPS LEADERBOARD & SECTORS GRID */}
              <div
                className="rw-bottom-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                  gap: 20,
                  alignItems: "start",
                }}
              >
                {/* GROUPS LEADERBOARD */}
                <GroupsLeaderboard
                  actors={filteredActors}
                  totalAttacks={totalAttacks}
                  selectedGroup={selectedGroup}
                  selectedSector={selectedSector}
                  onSelectGroup={(g) => setSelectedGroup(g === selectedGroup ? null : g)}
                  onOpenGroupDrawer={(g) => setDrawerGroup(g)}
                  onClearSectorFilter={() => setSelectedSector(null)}
                />

                {/* SECTORS PANEL */}
                <SectorsPanel
                  sectors={filteredSectors}
                  totalAttacks={totalAttacks}
                  selectedSector={selectedSector}
                  onSelectSector={(s) => setSelectedSector(s === selectedSector ? null : s)}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* GROUP DRAWER */}
      <GroupDrawer
        groupName={drawerGroup}
        timeframe={timeframe}
        onClose={() => setDrawerGroup(null)}
      />
    </section>
  );
}
